import dayjs from 'dayjs';
import {
  getBankFloorplanFacetCount,
  NO_FLOORPLAN,
} from 'scenes/ProjectBank/utils/bankFloorplans';

/**
 * How the media-tag facet combines the tags picked in it: a file linked to
 * any of them, or only a file linked to all of them.
 */
export const MEDIA_TAG_MATCH = { ANY: 'any', ALL: 'all' };

/**
 * The media-tag facet's mode: 'all' only when it says so. Anything else, a
 * filter state without the key included, is 'any'.
 * @param {*} value bankFilters.mediaTagMatch
 * @returns {string} one of MEDIA_TAG_MATCH
 */
export function normalizeMediaTagMatch(value) {
  return value === MEDIA_TAG_MATCH.ALL
    ? MEDIA_TAG_MATCH.ALL
    : MEDIA_TAG_MATCH.ANY;
}

/** Filter state with nothing active (mirrors the slice's bankFilters). */
export const EMPTY_BANK_FILTERS = {
  formatKeys: [],
  authorIds: [],
  dateRange: null,
  mediaTagIds: [],
  // How the picked tags combine; on its own it filters nothing.
  mediaTagMatch: MEDIA_TAG_MATCH.ANY,
  // The gallery view's floorplan facet (utils/bankFloorplans): '' for none,
  // NO_FLOORPLAN or a floorplan's id, and an area of that floorplan as the
  // map draws it, `{x, y, width, height}`.
  floorplanId: '',
  floorplanArea: null,
};

/** Quick presets for the "Lisätty" (added) date facet. */
export const DATE_PRESETS = [
  { key: 'today', labelKey: 'bank.date_preset_today' },
  { key: '7d', labelKey: 'bank.date_preset_7d' },
  { key: '30d', labelKey: 'bank.date_preset_30d' },
  { key: 'year', labelKey: 'bank.date_preset_year' },
  { key: 'all', labelKey: 'bank.date_preset_all' },
];

/**
 * Resolves a date preset into a `{start, end}` millisecond range: start is
 * the start of the lower-bound day ('7d' / '30d' = that many days back,
 * 'year' = 1 January), end is the end of today. 'all' (and unknown keys)
 * return null, meaning "no range".
 * @param {string} presetKey one of DATE_PRESETS keys
 * @param {number} [nowMs=Date.now()] reference time in ms
 * @returns {{start: number, end: number}|null}
 */
export function getDatePresetRange(presetKey, nowMs = Date.now()) {
  const now = dayjs(nowMs);
  const end = now.endOf('day').valueOf();
  switch (presetKey) {
    case 'today':
      return { start: now.startOf('day').valueOf(), end };
    case '7d':
      return { start: now.subtract(7, 'day').startOf('day').valueOf(), end };
    case '30d':
      return { start: now.subtract(30, 'day').startOf('day').valueOf(), end };
    case 'year':
      return { start: now.startOf('year').valueOf(), end };
    default:
      return null;
  }
}

/**
 * The preset whose range equals `range` for the reference time: 'all' for no
 * range, one of the DATE_PRESETS keys when the range matches that preset
 * exactly (start and end), null for a hand-picked range.
 * @param {{start?: number, end?: number}|null} range
 * @param {number} [nowMs=Date.now()] reference time in ms
 * @returns {string|null}
 */
export function getActiveDatePreset(range, nowMs = Date.now()) {
  if (!range || typeof range !== 'object') return 'all';
  const match = DATE_PRESETS.find(({ key }) => {
    const preset = getDatePresetRange(key, nowMs);
    return preset && preset.start === range.start && preset.end === range.end;
  });
  return match ? match.key : null;
}

const toBoundary = (value, fallback) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/**
 * A date range only filters when it does not cover every item in the folder:
 * DateRangeFilter fires the full `[bounds.min, bounds.max]` range on mount.
 * @param {{start?: number, end?: number}|null} range
 * @param {{min: number, max: number}|null} bounds createdAtMs bounds of the folder
 * @returns {boolean}
 */
export function isDateRangeActive(range, bounds) {
  if (!range || typeof range !== 'object') return false;
  if (!bounds || typeof bounds !== 'object') return false;
  if (!Number.isFinite(bounds.min) || !Number.isFinite(bounds.max)) {
    return false;
  }
  const start = toBoundary(range.start, -Infinity);
  const end = toBoundary(range.end, Infinity);
  return !(start <= bounds.min && end >= bounds.max);
}

/**
 * Normalizes a search box value for matching against item.searchText.
 * @param {string} text
 * @returns {string} trimmed, lowercased
 */
export function normalizeSearchText(text) {
  return typeof text === 'string' ? text.trim().toLowerCase() : '';
}

/**
 * The words of a search box value, matched one by one: an item passes when
 * every word is found in one of its searchable fields, so "kask ikkuna" is
 * Kask's photos tagged Ikkuna. A superset of matching the value whole — a
 * phrase that matched a name before still does, word by word.
 * @param {string} text
 * @returns {string[]} lowercased words; none while the box is blank
 */
export function searchTokensOf(text) {
  const query = normalizeSearchText(text);
  return query ? query.split(/\s+/) : [];
}

/**
 * Whether every search word is found somewhere on the item: its name
 * (`searchText`), its uploader (`searchAuthor`), its caption
 * (`searchDescription`) or the label of one of its tags (toBankItem).
 * @param {Object} item BankItem
 * @param {string[]} tokens searchTokensOf
 * @param {Object<string, string>} [mediaTagNamesById] tag id → lowercased label
 * @returns {boolean}
 */
const matchesSearchTokens = (item, tokens, mediaTagNamesById) => {
  const fields = [item.searchText, item.searchAuthor, item.searchDescription];
  if (mediaTagNamesById && Array.isArray(item.mediaTagIds)) {
    item.mediaTagIds.forEach((id) => fields.push(mediaTagNamesById[id]));
  }
  return tokens.every((token) =>
    fields.some((field) => typeof field === 'string' && field.includes(token))
  );
};

const hasEntries = (list) => Array.isArray(list) && list.length > 0;

/**
 * Number of active facets (type, author, media tags, date, the gallery
 * view's floorplan, and the AI conversation's photo filter), for the
 * "Suodattimet (n)" badge. Search text does not count, and the media tags
 * count once whether they match any or all (their mode alone is no facet).
 * The floorplan counts only where it is a facet (utils/bankFloorplans
 * getBankFloorplanFacetCount): never without `floorplanOptions`.
 * @param {Object} filters bankFilters
 * @param {{min: number, max: number}|null} bounds createdAtMs bounds of the folder
 * @param {?Object} [aiFilter] the AI photo filter, when one applies here
 * @param {{inScope?: boolean, isMapView?: boolean}} [floorplanOptions]
 * @returns {number}
 */
export function countActiveBankFilters(
  filters,
  bounds,
  aiFilter,
  floorplanOptions = {}
) {
  const active = filters || EMPTY_BANK_FILTERS;
  let count = 0;
  if (hasEntries(active.formatKeys)) count += 1;
  if (hasEntries(active.authorIds)) count += 1;
  if (hasEntries(active.mediaTagIds)) count += 1;
  if (isDateRangeActive(active.dateRange, bounds)) count += 1;
  count += getBankFloorplanFacetCount(active, floorplanOptions);
  if (aiFilter) count += 1;
  return count;
}

const toSet = (value) => {
  if (value instanceof Set) return value;
  return new Set(Array.isArray(value) ? value : []);
};

/**
 * Applies search text and the facet filters (AND across facets) to BankItems.
 * Returns the very same array reference when nothing is active so memoized
 * consumers keep their identity.
 * @param {Array<Object>} items BankItems
 * @param {Object} options
 * @param {Object} [options.filters] bankFilters
 * @param {string} [options.searchText] the search box: every word must be found in the item's name, uploader, caption or a tag's label (see searchTokensOf)
 * @param {Object<string, string>} [options.mediaTagNamesById] tag id → lowercased label, for the search to read an item's `mediaTagIds` by (selectBankMediaTagSearchNamesById); without it tags are not searched
 * @param {Set<string>|Array<string>} [options.tagFilteredFileIds] file ids the selected media tags keep, any or all of them as the facet says (selectBankTagFilteredFileIds)
 * @param {{min: number, max: number}|null} [options.bounds] createdAtMs bounds of the folder
 * @param {Set<string>|Array<string>} [options.aiFileIds] file ids the AI photo filter keeps; empty means no AI filter
 * @param {Set<string>|Array<string>} [options.aiFolderIds] folders that hold those photos, and their ancestors
 * @param {?(Set<string>|Array<string>)} [options.floorplanFileIds] the gallery view's floorplan facet: the photos placed on the chosen floorplan, or for NO_FLOORPLAN every placed photo (the facet keeps the others); null while the facet is off
 * @param {?(Set<string>|Array<string>)} [options.floorplanAreaFileIds] the photos in the area drawn on the chosen floorplan; null without one
 * @returns {Array<Object>}
 */
export function filterBankItems(items, options = {}) {
  const list = Array.isArray(items) ? items : [];
  const {
    filters,
    searchText,
    mediaTagNamesById,
    tagFilteredFileIds,
    bounds,
  } = options;
  const active = filters || EMPTY_BANK_FILTERS;
  // The floorplan facet keeps photos, so a folder never passes it.
  const floorplanFileIds = options.floorplanFileIds
    ? toSet(options.floorplanFileIds)
    : null;
  const keepsUnplaced = active.floorplanId === NO_FLOORPLAN;
  const floorplanAreaFileIds = options.floorplanAreaFileIds
    ? toSet(options.floorplanAreaFileIds)
    : null;
  // The AI filter is a set of photos, and a folder is not one of them — but a
  // folder the way to them is what the user needs to get there, so the folders
  // holding matches (and their ancestors, see the selector) come with it. A
  // folder outside that set goes, like any other non-match.
  const aiFileIds =
    options.aiFileIds && (options.aiFileIds.size || options.aiFileIds.length)
      ? toSet(options.aiFileIds)
      : null;
  const aiFolderIds = aiFileIds ? toSet(options.aiFolderIds) : null;

  const tokens = searchTokensOf(searchText);
  const formatKeys = hasEntries(active.formatKeys)
    ? new Set(active.formatKeys)
    : null;
  const authorIds = hasEntries(active.authorIds)
    ? new Set(active.authorIds)
    : null;
  const dateRange = isDateRangeActive(active.dateRange, bounds)
    ? {
        start: toBoundary(active.dateRange.start, -Infinity),
        end: toBoundary(active.dateRange.end, Infinity),
      }
    : null;
  const tagFileIds = hasEntries(active.mediaTagIds)
    ? toSet(tagFilteredFileIds)
    : null;

  if (
    !tokens.length &&
    !formatKeys &&
    !authorIds &&
    !dateRange &&
    !tagFileIds &&
    !aiFileIds &&
    !floorplanFileIds &&
    !floorplanAreaFileIds
  ) {
    return items;
  }

  return list.filter((item) => {
    if (aiFileIds) {
      const kept =
        item.type === 'FILE'
          ? aiFileIds.has(item.id)
          : aiFolderIds.has(item.id);
      if (!kept) return false;
    }
    if (
      tokens.length &&
      !matchesSearchTokens(item, tokens, mediaTagNamesById)
    ) {
      return false;
    }
    if (formatKeys && !formatKeys.has(item.formatKey)) return false;
    if (authorIds && !(item.author && authorIds.has(item.author.id))) {
      return false;
    }
    if (
      dateRange &&
      !(
        item.createdAtMs >= dateRange.start && item.createdAtMs <= dateRange.end
      )
    ) {
      return false;
    }
    if (tagFileIds && !(item.type === 'FILE' && tagFileIds.has(item.id))) {
      return false;
    }
    if (
      floorplanFileIds &&
      (item.type !== 'FILE' || floorplanFileIds.has(item.id) === keepsUnplaced)
    ) {
      return false;
    }
    if (
      floorplanAreaFileIds &&
      !(item.type === 'FILE' && floorplanAreaFileIds.has(item.id))
    ) {
      return false;
    }
    return true;
  });
}
